/**
 * @module SetUnitActions
 *
 * @description Complex actions that are used to operate the SetUnitSlice state.
 * Used to fill the edit modal,
 * To perform complicated field logic, scanning e.t.c.
 */
import { store, type AppDispatch, type RootState } from '../store'
import _ from 'lodash'
import {
    addOwner, type ModalRow, type Owner, type OwnerValues, type OwnerValuesKey,
    removeOwner,
    rerenderModal, setDynamicRowShow, setFooter,
    setImagesLoaded,
    setTitleDeedLoaded,
    setValue,
    detectChange,
    setRepresentative, addUploadedDocument, SpecialRequestSelectors,
    clearOwners
} from '../slices/setUnitSlice'

import { dataFetchingFinish, dataFetchingStart, setDocuments, setDynamicDropDown, setImages, setScannedData } from '../slices/modalSlice'
import { addProperty } from './propertyActions'
import { nanoid } from 'nanoid'
import { ACCESS_KEY, REACT_APP_BASE_URL } from '../../urls'
import searchFuse from '../../functions/search/searchFuse'
import { documentStatuses } from '../../app/custom/inputs/CustomEmiratesIdInput'
import dayjs from 'dayjs'
import { regExps } from '../../constants'
import filterUserCallback from '../../functions/shared/users/filterUserCallback'
import mapPropertiesForSelect from '../../functions/shared/properties/mapPropertiesForSelect'
import { addPropertyDeleteTask } from '../slices/propertySlice'
import { removeDataAttributes } from '../../functions/adapters/universal/removeDataAttributes'
import { scanDocuments } from './documentsActions/scaning/scanDocuments'
import { createDocument } from './documentsActions/createDocument'
import { hydrateUnitLandlords } from './ownersActions'
import {
    MAIN_UNIT_SELECTORS,
    UNIT_INFO_APPLICABILITY_FIELDS,
    normalizeCreateUnitInfoForStorage,
    resolveUnitInfoFieldApplicability,
    getSelectorsForTypeAndCategory,
    getUnitCategoriesByType,
    isMainUnitType
} from '../../functions/shared/units/mainUnitSelectors'
import { mapDisabledTimesToUi } from '../../functions/shared/units/disabledTimes'
import { getCorrectExpDate } from '../../functions/agent/date/getCorrectExpDateForScaning'

const clearInapplicableSetUnitFields = (dispatch: AppDispatch, getState: () => RootState, type: unknown, category: unknown): void => {
    const applicability = resolveUnitInfoFieldApplicability(type, category)
    const currentValues = getState().setUnit.staticForm.reduce((acc: Record<string, any>, item) => {
        if (item.id != null) acc[item.id] = item.value
        return acc
    }, {})
    const normalizedValues = normalizeCreateUnitInfoForStorage(currentValues, type, category)

    UNIT_INFO_APPLICABILITY_FIELDS.forEach((fieldId) => {
        if (applicability[fieldId] === false) {
            dispatch(setValue({ id: fieldId, value: normalizedValues[fieldId] }))
        }
    })

    dispatch(setDynamicRowShow({ id: 'GuestBathroom', state: applicability.GuestBathroom }))
    dispatch(setDynamicRowShow({ id: 'MaidsRoom', state: applicability.MaidsRoom }))
}

const getDocumentsArray = (documents: any): any[] => {
    if (Array.isArray(documents)) return documents
    if (Array.isArray(documents?.data)) return documents.data
    return []
}

const getDocumentKind = (document: any): 'passport' | 'emirates_id' | null => {
    const fields = document?.attributes ?? document ?? {}
    const category = String(fields?.Category ?? '').toLowerCase().trim()
    if (category === 'passport') return 'passport'
    if (category === 'emirates id') return 'emirates_id'

    const component = String(fields?.Type?.[0]?.__component ?? '').toLowerCase()
    if (component.includes('passport')) return 'passport'
    if (component.includes('emirates-id')) return 'emirates_id'

    return null
}

const pickOwnerDocument = (documents: any, kind: 'passport' | 'emirates_id'): any | undefined => {
    const docsArray = getDocumentsArray(documents)
    return docsArray.find((doc) => getDocumentKind(doc) === kind)
}

const ownerDocumentFields = (document: any): any => document?.attributes ?? document ?? {}

const ownerIdentityConflictKeys = (values: OwnerValues): Array<'DateOfBirth' | 'Country' | 'Gender'> => {
    const fields: Array<['DateOfBirth' | 'Country' | 'Gender', string]> = [
        ['DateOfBirth', 'date_of_birth'],
        ['Country', 'nationality'],
        ['Gender', 'gender']
    ]
    return fields
        .filter(([, documentKey]) => {
            const passportValue = String(values[`passport_${documentKey}` as OwnerValuesKey] ?? '').trim()
            const emiratesValue = String(values[`emirates_id_${documentKey}` as OwnerValuesKey] ?? '').trim()
            return passportValue !== '' && emiratesValue !== '' && passportValue !== emiratesValue
        })
        .map(([contactKey]) => contactKey)
}

const reconcileOwnerIdentityConflicts = (ownerId: string) => (dispatch: AppDispatch, getState: () => RootState): void => {
    const owner = getState().setUnit.owners.find((item) => item.id === ownerId)
    if (owner == null) return
    const conflicts = ownerIdentityConflictKeys(owner.values)
    conflicts.forEach((contactKey) => dispatch(setValue({ id: ownerFieldId(ownerId, 'ContactInfo', contactKey), value: '' })))
    if (conflicts.length > 0) dispatch(rerenderModal())
}

/**
 * @function renderSetUnitModal
 * @param {string} modalID
 *
 * @description This function is used to get the modal object that is filled with all values and callbacks based on SetUnitSlice. Runs on every render of SetUnitModal component
 */
export const renderSetUnitModal = (modalID: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setFooter(modalID))
    const modal = _.cloneDeep(getState().setUnit.modal)
    const propertyId = getState().setUnit.staticForm.find((formItem) => formItem.id === 'Property')?.value
    const selectedType = getState().setUnit.staticForm.find((formItem) => formItem.id === 'Type')?.value
    const selectedCategory = getState().setUnit.staticForm.find((formItem) => formItem.id === 'UnitCategory')?.value
    const unitInfoFieldApplicability = resolveUnitInfoFieldApplicability(selectedType, selectedCategory)
    const unitConfig = store.getState().app.config?.units



    // export const unitCategories = {} // config?. units?.unitCategories 
    // const isCommercial = currentUnit?.attributes?.Type === 'Commercial'
    // isCommercial ? unitCategories.Commercial : unitCategories.Residential;

    let property: any = null
    if (propertyId != null) {
        property = getState().property.properties?.find((property) => property.id === propertyId)
    }
    _.forEach(getState().setUnit.staticForm, (formItem) => {
        if (formItem.userType != null && getState().user.currentUser.Category !== formItem.userType) {
            return
        }
        if (
            formItem.id != null &&
            UNIT_INFO_APPLICABILITY_FIELDS.includes(formItem.id as any) &&
            unitInfoFieldApplicability[formItem.id as keyof typeof unitInfoFieldApplicability] === false
        ) {
            return
        }
        switch (formItem.row.element) {
            case 'document-scan-input':
                // eslint-disable-next-line no-case-declarations
                modal.form.push({
                    ...formItem.row,
                    onScan: (documentType: 'Title Deed') => async (documents: Record<string, File>): Promise<{ isSuccessful: boolean }> => {
                        dispatch(dataFetchingStart())
                        try {
                            const previousDocuments = _.cloneDeep(getState().modal.documents ?? [])
                            const response = await dispatch(scanDocuments(Object.values(documents), 'Title Deed'))

                            const scannedDocuments = _.cloneDeep(getState().modal.documents ?? [])
                            dispatch(setDocuments(_.uniq([...previousDocuments, ...scannedDocuments])))

                            const fileUrl: string | null = response?.[0]?.url
                            const failedToScanUrl = getState().modal.scannedData?.failedToScan?.url
                            if (fileUrl != null && (scannedDocuments.length > 0 || failedToScanUrl != null)) {
                                dispatch(addUploadedDocument({ field: String(formItem.id), base64: fileUrl }))
                                dispatch(applyTitleDeed(response))
                                dispatch(setTitleDeedLoaded(true))
                                dispatch(rerenderModal())
                                dispatch(setValue({ value: documentStatuses.true, id: formItem.id ?? '' }))
                                dispatch(detectChange(true))
                                dispatch(dataFetchingFinish())
                                return {
                                    isSuccessful: true
                                }
                            }
                            dispatch(dataFetchingFinish())
                        } catch (err) {
                            dispatch(dataFetchingFinish())
                            console.log('Error while title deed scanning', err)
                        }
                        return {
                            isSuccessful: false
                        }
                    },
                    value: formItem.value
                })
                break
            case 'input':
            case 'input-group':
                switch (formItem.id) {
                    case 'Bathrooms': {
                        const categoryRow = getState().setUnit.staticForm.find(row => row.id === 'Type')
                        modal.form.push({
                            ...formItem.row,
                            content: (
                                categoryRow?.value !== 'Residential'
                                    ? ['Number of Restrooms', 'Enter the number of restrooms']
                                    : formItem.row.content
                            ),
                            onInput: (e: any) => {
                                dispatch(setValue({ value: e.target.value, id: formItem.id ?? '' }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    }
                    case 'Bedrooms': {
                        const categoryRow = getState().setUnit.staticForm.find(row => row.id === 'Type')
                        modal.form.push({
                            ...formItem.row,
                            content: (
                                categoryRow?.value !== 'Residential'
                                    ? ['Number of Rooms', 'Enter the number of rooms']
                                    : formItem.row.content
                            ),
                            onInput: (e: any) => {
                                dispatch(setValue({ value: e.target.value, id: formItem.id ?? '' }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    }
                    default:
                        modal.form.push({
                            ...formItem.row,
                            onInput: (e: any) => {
                                dispatch(setValue({ value: e.target.value, id: formItem.id ?? '' }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                }
                break
            case 'input-native-date':
            case 'input-native-year':
                modal.form.push({
                    ...formItem.row,
                    defaultValue: formItem.value,
                    onInput: (event: any) => {
                        dispatch(setValue({
                            value: event.target.value,
                            id: formItem.id ?? ''
                        }))
                        dispatch(detectChange(true))
                    }
                })
                break
            case 'select':
                switch (formItem.id) {
                    // case 'Property':
                    //     modal.form.push({
                    //         ...formItem.row,
                    //         selectors: getState().property.properties?.map(property => ({
                    //             label: property.attributes.Name,
                    //             value: property.id
                    //         })),
                    //         onChange: (e: any) => {
                    //             dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                    //             dispatch(detectChange(true))
                    //         }
                    //     })
                    //     break
                    case 'Features':
                    case 'Amenities':
                    case 'Appliances':
                    {
                        const selectedType = getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === 'Type')?.value
                        const selectedCategory = getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === 'UnitCategory')?.value
                        const selectorKey = formItem.id as 'Features' | 'Amenities' | 'Appliances'
                        modal.form.push({
                            ...formItem.row,
                            selectors: getSelectorsForTypeAndCategory(selectedType, selectedCategory, selectorKey),
                            onChange: (e: any) => {
                                dispatch(setValue({
                                    value: [
                                        ...(getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === formItem.id)?.value ?? []),
                                        e
                                    ],
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            },
                            onDeselect: (e: any) => {
                                dispatch(setValue({
                                    value: (getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === formItem.id)?.value ?? []).filter((value: any) => value !== e),
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    }
                    case 'UnitCategory':
                    {
                        const selectedType = getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === 'Type')?.value
                        const categories = getUnitCategoriesByType(selectedType)
                        modal.form.push({
                            ...formItem.row,
                            selectors: categories,
                            onChange: (e: any) => {
                                dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                                clearInapplicableSetUnitFields(dispatch, getState, selectedType, e)
                                dispatch(detectChange(true))
                                dispatch(rerenderModal())
                            }
                        })
                        break
                    }
                    case 'tenant_requirements':
                        modal.form.push({
                            ...formItem.row,
                            selectors: SpecialRequestSelectors,
                            onChange: (e: any) => {
                                dispatch(setValue({
                                    value: [
                                        ...(getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === formItem.id)?.value ?? []),
                                        e
                                    ],
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            },
                            onDeselect: (e: any) => {
                                dispatch(setValue({
                                    value: (getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === formItem.id)?.value ?? []).filter((value: any) => value !== e),
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    case 'DisabledWeekdays':
                        modal.form.push({
                            ...formItem.row,
                            onChange: (e: any) => {
                                dispatch(setValue({
                                    value: [
                                        ...(getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === formItem.id)?.value ?? []),
                                        e
                                    ],
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            },
                            onDeselect: (e: any) => {
                                dispatch(setValue({
                                    value: (getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === formItem.id)?.value ?? []).filter((value: any) => value !== e),
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    case 'Fit':
                    case 'View':
                    {
                        const selectedType = getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === 'Type')?.value
                        const selectedCategory = getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === 'UnitCategory')?.value
                        const selectorKey = formItem.id as 'Fit' | 'View'
                        modal.form.push({
                            ...formItem.row,
                            selectors: getSelectorsForTypeAndCategory(selectedType, selectedCategory, selectorKey),
                            onChange: (e: any) => {
                                dispatch(setValue({
                                    value: e,
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    }
                    case 'Statuses':
                        modal.form.push({
                            ...formItem.row,
                            onChange: (e: any) => {
                                /** Add field Occupied until to form in case status is set to occupied */
                                if (e?.includes('Occupied') === true) {
                                    dispatch(setDynamicRowShow({ id: 'OccupiedUntil', state: true }))
                                    dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                                    dispatch(detectChange(true))
                                    dispatch(rerenderModal())
                                } else {
                                    dispatch(setDynamicRowShow({ id: 'OccupiedUntil', state: false }))
                                    dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                                    dispatch(detectChange(true))
                                    dispatch(rerenderModal())
                                }
                            }
                        })
                        break

                    case 'titleDeedPropertyType':
                        modal.form.push({
                            ...formItem.row,
                            selectors: getState().app.config?.documents?.Type?.TitleDeed?.Type ?? [],
                            onChange: (e: any) => {
                                dispatch(setValue({
                                    value: e,
                                    id: formItem.id ?? ''
                                }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                    case 'Type':
                        modal.form.push({
                            ...formItem.row,
                            selectors: [...MAIN_UNIT_SELECTORS.Type],
                            onChange: (e: any) => {
                                const availableCategories = getUnitCategoriesByType(e)
                                const currentCategory = getState().setUnit.staticForm.find((storedFormItem) => storedFormItem.id === 'UnitCategory')?.value
                                let nextCategory = currentCategory
                                if (currentCategory != null && !availableCategories.includes(currentCategory)) {
                                    dispatch(setValue({ value: null, id: 'UnitCategory' }))
                                    nextCategory = null
                                }

                                dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                                clearInapplicableSetUnitFields(dispatch, getState, e, nextCategory)
                                dispatch(detectChange(true))
                                dispatch(rerenderModal())
                            }
                        })
                        break
                    default:
                        modal.form.push({
                            ...formItem.row,
                            onChange: (e: any) => {
                                dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                                dispatch(detectChange(true))
                            }
                        })
                        break
                }
                break
            case 'select-add':
                modal.form.push({
                    ...formItem.row,
                    onChange: (e: any) => {
                        const previousPropertyId = getState().setUnit.staticForm.find((row) => row.id === 'Property')?.value

                        if (previousPropertyId != null) {
                            dispatch(addPropertyDeleteTask({
                                id: previousPropertyId
                            }))
                        }
                        dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                        dispatch(detectChange(true))
                        dispatch(rerenderModal())
                    },
                    modal: 'set_property_modal',
                    editModal: property != null ? 'edit_property_modal' : undefined
                })
                break
            case 'button':
                switch (formItem.id) {
                    case 'add_owner_button':
                        modal.form.push({
                            ...formItem.row,
                            onClick: () => {
                                dispatch(detectChange(true))
                                dispatch(addOwnerToForm())
                            }
                        })
                        break
                    case 'add_poa_button':
                        // eslint-disable-next-line no-case-declarations
                        const poaSectionOpen = getState().setUnit.dynamicForm.find((formItem) => formItem.id === 'representative.ContactInfo.Name')?.show === true
                        modal.form.push({
                            ...formItem.row,
                            content: [poaSectionOpen ? 'Remove POA' : 'Add POA'],
                            onClick: () => {
                                dispatch(detectChange(true))
                                dispatch(setDynamicRowShow({ id: 'representative.ContactInfo.Name', state: !poaSectionOpen }))
                                dispatch(setDynamicRowShow({ id: 'representative.ContactInfo.Phone', state: !poaSectionOpen }))
                                dispatch(setDynamicRowShow({ id: 'representative.ContactInfo.Email', state: !poaSectionOpen }))
                                dispatch(setDynamicRowShow({ id: 'representative.emirates_id', state: !poaSectionOpen }))
                                dispatch(setDynamicRowShow({ id: 'representative.passport', state: !poaSectionOpen }))
                                dispatch(setDynamicRowShow({ id: 'representative.POA', state: !poaSectionOpen }))
                                dispatch(setDynamicRowShow({ id: 'representative.separator', state: !poaSectionOpen }))
                                dispatch(rerenderModal())
                            }
                        })
                        break
                }
                break
            case 'label-badge':
                switch (formItem.id) {
                    case 'PropertyInfo':
                        if (property != null) {
                            modal.form.push({
                                ...formItem.row,
                                content: [
                                    {
                                        label: 'Property Name',
                                        text: property.attributes.Name
                                    },
                                    {
                                        label: 'Property Location',
                                        text: property.attributes.Area
                                    },
                                    {
                                        label: 'Property Type',
                                        text: property.attributes.Type
                                    }
                                ]
                            })
                        }
                        break
                    default:
                        modal.form.push(formItem.row)
                        break
                }
                break
            case 'hidden-input':
                switch (formItem.id) {
                    case 'property_valid':
                        modal.form.push({
                            ...formItem.row,
                            required: property?.attributes?.Area == null ? formItem.row.required : undefined
                        })
                        break
                    default:
                        modal.form.push(formItem.row)
                        break
                }
                break
            default:
                modal.form.push(formItem.row)
                break
        }
    })
    const ownerRows: any[] = []
    _.forEach(getState().setUnit.owners, (owner) => {
        const ownerFields = mapOwnerToTemplate(owner)
        ownerRows.push({
            ...ownerFields[0].row,
            onRemove: () => {
                dispatch(removeOwner(owner.id))
                if (getState().setUnit.owners.length === 0) {
                    dispatch(setValue({ id: 'owners_valid', value: '' }))
                }
                dispatch(detectChange(true))
                dispatch(rerenderModal())
            },
            onInput: (e: any) => {
                dispatch(detectChange(true))
                dispatch(setValue({ value: e.target.value, id: ownerFieldId(owner.id ?? '', ...ownerFieldParts[0]) }))
            }
        })
        for (let i = 1; i < ownerFields.length; i++) {
            const fieldId = String(ownerFields[i]?.row?.id ?? '')
            if (ownerFields[i]?.row?.element === 'owner-identity-documents') {
                ownerRows.push({
                    ...ownerFields[i].row,
                    ownerIdentityDocuments: {
                        ...ownerFields[i].row.ownerIdentityDocuments,
                        onAdd: (kind: 'passport' | 'emirates_id') => {
                            dispatch(detectChange(true))
                            dispatch(setValue({ id: ownerFieldId(owner.id, `${kind}_enabled`), value: true }))
                            dispatch(setValue({ id: ownerFieldId(owner.id, `${kind}_details_open`), value: true }))
                            dispatch(rerenderModal())
                        },
                        onToggle: (kind: 'passport' | 'emirates_id') => {
                            const current = getState().setUnit.owners.find((item) => item.id === owner.id)?.values
                            dispatch(setValue({ id: ownerFieldId(owner.id, `${kind}_details_open`), value: current?.[`${kind}_details_open` as OwnerValuesKey] !== true }))
                            dispatch(rerenderModal())
                        },
                        onFile: (kind: 'passport' | 'emirates_id', side: 'single' | 'front' | 'back', files: FileList | null) => {
                            const file = files?.[0]
                            if (file == null) return
                            const key = kind === 'passport' ? 'passport_file' : `emirates_id_${side}_file`
                            dispatch(detectChange(true))
                            dispatch(setValue({ id: ownerFieldId(owner.id, key), value: file }))
                            dispatch(setValue({ id: ownerFieldId(owner.id, `${kind}_enabled`), value: true }))
                            dispatch(setValue({ id: ownerFieldId(owner.id, `${kind}_details_open`), value: true }))
                            void dispatch(scanOwnerIdentityDocument(owner.id, kind))
                            dispatch(rerenderModal())
                        },
                        onUseSingleFile: (enabled: boolean) => {
                            dispatch(detectChange(true))
                            dispatch(setValue({ id: ownerFieldId(owner.id, 'emirates_id_use_single_file'), value: enabled }))
                            if (enabled) void dispatch(scanOwnerIdentityDocument(owner.id, 'emirates_id'))
                            dispatch(rerenderModal())
                        },
                        onFieldChange: (kind: 'passport' | 'emirates_id', key: string, value: string) => {
                            dispatch(detectChange(true))
                            dispatch(setValue({ id: ownerFieldId(owner.id, `${kind}_${key}`), value }))
                            if (['date_of_birth', 'nationality', 'gender'].includes(key)) dispatch(reconcileOwnerIdentityConflicts(owner.id))
                        },
                        onFieldCommit: () => {
                            dispatch(rerenderModal())
                        },
                        onConflictResolve: (key: 'DateOfBirth' | 'Country' | 'Gender', value: string) => {
                            dispatch(detectChange(true))
                            dispatch(setValue({ id: ownerFieldId(owner.id, 'ContactInfo', key), value }))
                            dispatch(rerenderModal())
                        }
                    }
                })
                continue
            }
            const ownerFileKey = ownerIdentityFileKeys.find((key) => fieldId.endsWith(`.${key}`))
            if (ownerFileKey == null) {
                if (ownerFields[i]?.row?.id?.includes('IsDecisionMaker') === true) {
                    ownerRows.push({
                        ...ownerFields[i].row,
                        onChange: (e: any) => {
                            dispatch(detectChange(true))
                            dispatch(setValue({
                                value: e,
                                id: fieldId
                            }))
                            if (e === 'Yes') {
                                getState().setUnit.owners.forEach((editedOwner) => {
                                    if (editedOwner.id !== owner.id) {
                                        dispatch(setValue({
                                            value: 'No',
                                            id: ownerFieldId(editedOwner.id ?? '', 'IsDecisionMaker')
                                        }))
                                        dispatch(rerenderModal())
                                    }
                                })
                            }
                        }
                    })
                } else {
                    ownerRows.push({
                        ...ownerFields[i].row,
                        onInput: (e: any) => {
                            dispatch(detectChange(true))
                            dispatch(setValue({
                                value: e.target.value,
                                id: fieldId
                            }))
                            if (['passport_date_of_birth', 'passport_nationality', 'passport_gender', 'emirates_id_date_of_birth', 'emirates_id_nationality', 'emirates_id_gender'].some((key) => fieldId.endsWith(`.${key}`))) {
                                dispatch(reconcileOwnerIdentityConflicts(owner.id))
                            }
                        }
                    })
                }
            } else {
                const documentCategory = ownerFileKey === 'passport_file' ? 'Passport' : 'Emirates ID'
                const existingLink = ownerFileKey === 'passport_file'
                    ? owner.values.uploaded_passport
                    : ownerFileKey === 'emirates_id_front_file'
                        ? owner.values.uploaded_emirates_id_front ?? owner.values.uploaded_emirates_id
                        : owner.values.uploaded_emirates_id_back
                ownerRows.push({
                    ...ownerFields[i].row,
                    required: false,
                    ownerIdentity: true,
                    selectedFile: owner.values[ownerFileKey],
                    uploaded: owner.values[ownerFileKey] != null || existingLink != null,
                    uploadedBase64: existingLink,
                    onInput: (e: any) => {
                        dispatch(detectChange(true))
                        const file = e.target.files?.[0]
                        if (file == null) return
                        dispatch(setValue({ value: file, id: ownerFieldId(owner.id ?? '', ownerFileKey) }))
                        void dispatch(scanOwnerIdentityDocument(owner.id, documentCategory === 'Passport' ? 'passport' : 'emirates_id'))
                    }
                })
            }
        }
        try {
            const index = _.findIndex(ownerRows, (formRow: any) => formRow.id === ownerFieldId(owner.id ?? '', ...ownerFieldParts[0]))
            ownerRows[index].onAdd = undefined
        } catch (err) {
            console.error(err)
        }
    })
    
    const poaIndex = _.findIndex(modal.form, (formRow: any) => formRow.id === 'add_poa_button')
    modal.form = [
        ..._.slice(modal.form, 0, poaIndex),
        ...ownerRows,
        ..._.slice(modal.form, poaIndex)
    ]
    const index = _.findLastIndex(modal.form, (formRow: any) => _.includes(formRow.id, 'Owners[') && _.includes(formRow.id, 'Name'))
    if (index != null && modal.form[index] != null) {
        modal.form[index].onAdd = () => {
            dispatch(detectChange(true))
            dispatch(addOwnerToForm())
        }
        const buttonIndex = _.findIndex(modal.form, (formRow: any) => formRow.id === 'add_owner_button')
        modal.form.splice(buttonIndex, 1)
    }
    _.forEach(
        _.filter(getState().setUnit.dynamicForm, (formItem) => formItem.show),
        (formItem) => {
            let row: any
            switch (formItem.row.element) {
                case 'button':
                    row = {
                        ...formItem.row,
                        onClick: () => {

                        }
                    }
                    break
                case 'input-file':
                    switch (formItem.id) {
                        case 'representative.emirates_id':
                        case 'representative.passport':
                        case 'representative.POA':
                            // eslint-disable-next-line no-case-declarations
                            const representative = getState().setUnit.representative
                            row = {
                                ...formItem.row,
                                uploadedBase64: getState().setUnit.uploadedDocuments[formItem.id],
                                required: formItem.id === 'representative.POA'
                                    ? representative.poa == null
                                    : representative.passport == null && representative.emirates_id == null,
                                uploaded: (representative.passport != null && formItem.id === 'representative.passport') || (representative.emirates_id != null && formItem.id === 'representative.emirates_id') || (representative.poa != null && formItem.id === 'representative.POA'),
                                onInput: (e: any) => {
                                    const files = e.target.files
                                    dispatch(dataFetchingStart())
                                    dispatch(detectChange(true))

                                    const RefID = `REF_DOC<->${formItem.id === 'representative.passport'
                                            ? 'passport'
                                            : formItem.id === 'representative.emirates_id'
                                                ? 'emirates_id'
                                                : 'power_of_attorney'
                                        }<->${nanoid()}`

                                    const Category = formItem.id === 'representative.passport'
                                        ? 'Passport'
                                        : formItem.id === 'representative.emirates_id'
                                            ? 'Emirates ID'
                                            : 'Power Of Attorney'

                                    const Type = [{
                                        __component: `documents.${formItem.id === 'representative.passport'
                                                ? 'passport'
                                                : formItem.id === 'representative.emirates_id'
                                                    ? 'emirates-id'
                                                    : 'power-of-attorney'
                                            }`
                                    }]

                                    void dispatch(createDocument(files, {
                                        RefID,
                                        Category,
                                        Type
                                    }, undefined, undefined, undefined, RefID))
                                        .then((response: any) => {
                                    
                                            const fileUrl: string | null = response?.response?.data?.attributes?.Link
                                            if (fileUrl != null) {
                                                dispatch(addUploadedDocument({ field: String(formItem.id), base64: fileUrl }))
                                            }
                                            const key = formItem.id === 'representative.passport'
                                                ? 'passport'
                                                : formItem.id === 'representative.emirates_id'
                                                    ? 'emirates_id'
                                                    : 'poa'
                                            dispatch(setRepresentative({
                                                ...getState().setUnit.representative,
                                                [key]: response.response.data.id
                                            }))
                                            dispatch(rerenderModal())
                                        })
                                        .finally(() => {
                                            dispatch(dataFetchingFinish())
                                        })
                                }
                            }
                            break
                        default:
                            row = formItem.row
                            break
                    }
                    break
                case 'input':
                    row = {
                        ...formItem.row,
                        onInput: (e: any) => {
                          
                            dispatch(setValue({ value: e.target.value, id: formItem.id ?? '' }))
                            dispatch(detectChange(true))
                        }
                    }
                    break
                case 'input-date':
                    row = {
                        ...formItem.row,
                        onChange: (value: any) => {
                            dispatch(setValue({ value: value.toISOString(), id: formItem.id ?? '' }))
                            dispatch(detectChange(true))
                        }
                    }
                    break
                case 'select':
                    switch (formItem.id) {

                        case 'GuestBathroom':
                        case 'MaidsRoom':
                            row = {
                                ...formItem.row,
                                onChange: (e: any) => {
                                    dispatch(setValue({ value: e, id: formItem.id ?? '' }))
                                    dispatch(detectChange(true))
                                }
                            }
                            break
                        default:
                            row = formItem.row
                            break
                    }
                    break
                default:
                    row = formItem.row
                    break
            }
            if (formItem.placeAfter != null) {
                const index = _.findIndex(modal.form, (formRow: any) => formRow?.id === formItem.placeAfter)
                modal.form = [
                    ..._.slice(modal.form, 0, index + 1),
                    row,
                    ..._.slice(modal.form, index + 1)
                ]
            } else if (formItem.placeBefore != null) {
                const index = _.findIndex(modal.form, (formRow: any) => formRow?.id === formItem.placeAfter)
                modal.form = [
                    ..._.slice(modal.form, 0, index),
                    row,
                    ..._.slice(modal.form, index)
                ]
            } else {
                modal.form.push(row)
            }
        }
    )
    return modal
}

/**
 * @function renderSetUnitInitialState
 * @description returns the initial state of the modal based on SetUnitSlice that will be passed to react hook form as initial state
 */
export const renderSetUnitInitialState = () => (dispatch: AppDispatch, getState: () => RootState) => {
    let initialState: any = {}
    _.forEach(getState().setUnit.staticForm, (formItem) => {
        if (formItem.value != null && formItem.id != null) {
            initialState[formItem.id] = formItem.value
        }
    })
    _.forEach(getState().setUnit.owners, (owner) => {
        if (owner.values != null && owner.id != null) {
            for (const fieldPart of ownerFieldParts) {
                if (owner.values[fieldPart[fieldPart.length - 1] as OwnerValuesKey] != null) {
                    if (_.isBoolean(owner.values[fieldPart[fieldPart.length - 1] as OwnerValuesKey])) {
                        initialState[ownerFieldId(owner.id, ...fieldPart)] = owner.values[fieldPart[fieldPart.length - 1] as OwnerValuesKey] === true ? 'Yes' : 'No'
                    } else {
                        initialState[ownerFieldId(owner.id, ...fieldPart)] = owner.values[fieldPart[fieldPart.length - 1] as OwnerValuesKey]
                    }
                }
            }
        }
    })
    _.forEach(getState().setUnit.dynamicForm, (formItem) => {
        if (formItem.value != null && formItem.id != null && formItem.show) {
            switch (formItem.row.element) {
                case 'input-date':
                    initialState[formItem.id] = dayjs(formItem.value)
                    break
                default:
                    initialState[formItem.id] = formItem.value
                    break
            }
        }
    })
    initialState = {
        ...initialState,
        ...getState().setUnit.documentFields
    }
    return initialState
}

const ownerIdentityFileKeys = ['passport_file', 'emirates_id_front_file', 'emirates_id_back_file'] as const

const normalizeOwnerScanDate = (value: unknown): string | undefined => {
    const source = String(value ?? '').trim()
    if (source === '') return undefined
    const parsed = dayjs(source)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : source
}

/** Reads an owner identity document without creating a persistent Document record. */
export const scanOwnerIdentityDocument = (ownerId: string, kind: 'passport' | 'emirates_id') => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const owner = getState().setUnit.owners.find((item) => item.id === ownerId)
    if (owner == null) return
    const files = kind === 'passport'
        ? [owner.values.passport_file]
        : [owner.values.emirates_id_front_file, owner.values.emirates_id_back_file ?? (owner.values.emirates_id_use_single_file === true ? owner.values.emirates_id_front_file : undefined)]
    if (files.some((file) => file == null)) return

    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const scanOne = async (file: File, type: 'Passport' | 'Emirates ID' | 'Emirates ID Front'): Promise<any | null> => {
        const body = new FormData()
        body.append('file', file)
        body.append('type', type)
        const response = await fetch(`${REACT_APP_BASE_URL}/api/service/scanDocument`, { method: 'POST', body, headers: { Authorization: `Bearer ${jwt}` } })
        const json = await response.json()
        return json?.response?.error == null ? json?.response?.document ?? null : null
    }

    dispatch(dataFetchingStart())
    try {
        const scanned = kind === 'passport'
            ? await scanOne(files[0] as File, 'Passport')
            : (await scanOne(files[1] as File, 'Emirates ID')) ?? await scanOne(files[0] as File, 'Emirates ID Front')
        if (scanned == null) return

        const prefix = kind === 'passport' ? 'passport' : 'emirates_id'
        const scannedValues: Record<string, string | undefined> = {
            [`${prefix}_number`]: scanned.number != null ? String(scanned.number) : undefined,
            [`${prefix}_name`]: scanned.name != null ? String(scanned.name) : undefined,
            [`${prefix}_date_of_birth`]: normalizeOwnerScanDate(scanned.dob),
            [`${prefix}_gender`]: scanned.gender === 'M' ? 'Male' : scanned.gender === 'F' ? 'Female' : scanned.gender,
            [`${prefix}_nationality`]: scanned.nationality,
            [`${prefix}_expiry_date`]: normalizeOwnerScanDate(scanned.expDate)
        }
        Object.entries(scannedValues).forEach(([key, value]) => {
            if (value != null && String((owner.values as any)[key] ?? '').trim() === '') dispatch(setValue({ id: ownerFieldId(ownerId, key), value }))
        })
        const contactValues: Record<string, string | undefined> = {
            Name: scannedValues[`${prefix}_name`],
            DateOfBirth: scannedValues[`${prefix}_date_of_birth`],
            Gender: scannedValues[`${prefix}_gender`],
            Country: scannedValues[`${prefix}_nationality`]
        }
        Object.entries(contactValues).forEach(([key, value]) => {
            if (value != null && String((owner.values as any)[key] ?? '').trim() === '') dispatch(setValue({ id: ownerFieldId(ownerId, 'ContactInfo', key), value }))
        })
        const documentFieldsComplete = ['number', 'name', 'date_of_birth', 'gender', 'nationality', 'expiry_date']
            .every((key) => String(getState().setUnit.owners.find((item) => item.id === ownerId)?.values[`${prefix}_${key}` as OwnerValuesKey] ?? '').trim() !== '')
        if (documentFieldsComplete) dispatch(setValue({ id: ownerFieldId(ownerId, `${prefix}_details_open`), value: false }))
        dispatch(reconcileOwnerIdentityConflicts(ownerId))
        dispatch(rerenderModal())
    } catch (error) {
        console.error('Unable to scan owner identity document', error)
    } finally {
        dispatch(dataFetchingFinish())
    }
}

/**
 * @function fillValuesForEdit
 * @param {number} unitID
 *
 * @description for each field of the set unit modal finds value in unit object stored in state and calls setValue function to fill the value in SetUnitState.
 * After all values are filled calls rerenderModal to render the filled modal to user
 */
export const fillValuesForEdit =  (unitID: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    // const { currentUser } = getState().user
  dispatch(clearOwners())
    const units = getState().units.units
    const unit = units.find(unitItem => unitItem?.id === unitID)
    let currentUnit = removeDataAttributes(_.cloneDeep(unit ?? {}))
    // Unit list payloads may include ContactInfo but omit Document.Type. The edit form
    // always hydrates each linked landlord so identity fields come from the actual document.
    currentUnit = removeDataAttributes(await hydrateUnitLandlords(currentUnit))

    const { staticForm } = getState().setUnit
    _.forEach(staticForm, (formItem) => {
        switch (formItem.id) {
            case 'title_deed':
                dispatch(setValue({ id: 'title_deed', value: documentStatuses.true }))
                break

            case 'Property':
                dispatch(setValue({ id: 'Property', value: (currentUnit)?.Property?.id ?? (currentUnit)?.Property?.data?.id }))
                break
            case 'UnitCategory':
                dispatch(setValue({
                    id: 'UnitCategory',
                    value: currentUnit?.Category ?? null
                }))
                break
            case 'Statuses':
                // case 'Statuses[2]':
                for (const selector of formItem.row.selectors as Array<{ value: string }> ?? []) {
                    const splitSelector = selector?.value?.split('-')
                    if (splitSelector?.every((selectorItem) => (currentUnit)?.Statuses?.includes(selectorItem))) {
                        dispatch(setValue({ id: formItem.id as string, value: selector?.value }))
                    }
                }
                /* _.forEach((currentUnit)?.Statuses ?? [], (status: string) => {
                    //   console.log(formItem.row.selectors, status)
                    if (_.includes(formItem.row.selectors as string[], status)) {
                        dispatch(setValue({ id: formItem.id as string, value: status }))
                    }
                }) */
                break
            case 'Statuses[1]':
                if (_.includes((currentUnit)?.Statuses ?? [], (formItem.row.selectors?.[0] as { label: string, value: string })?.value)) {
                    dispatch(setValue({ id: 'Statuses[1]', value: (formItem.row.selectors?.[0] as { label: string, value: string })?.value }))
                } else {
                    dispatch(setValue({ id: 'Statuses[1]', value: (formItem.row.selectors?.[1] as { label: string, value: string })?.value }))
                }
                break
            // case 'GuestBathroom':
            // case 'MaidsRoom':

            case 'Bedrooms':
                dispatch(setValue({
                    id: formItem.id,
                    value: currentUnit?.Bedrooms === 0 ? 'Studio' : currentUnit?.Bedrooms
                }))
                break
            case 'DisabledTimes':
                dispatch(setValue({
                    id: formItem.id,
                    value: mapDisabledTimesToUi(currentUnit?.DisabledTimes ?? [])
                }))
                break
            case 'AdvertisementSpaces':
                dispatch(setValue({
                    id: formItem.id,
                    value: currentUnit.AdvertisementSpaces != null ? String(currentUnit.AdvertisementSpaces) : 'Open'
                }))
                break
            case 'titleDeedPropertyType':
                {
                    const unitDocsIDs = currentUnit?.Documents?.map((doc: any) => doc.id) ?? []
                    const unitTitleDeedDocs = getState().documents.documents.filter((doc) => unitDocsIDs.includes(doc.id) === true && doc?.attributes?.Type?.[0]?.__component === 'documents.title-deed')
                    const titleDeedDoc = unitTitleDeedDocs.length > 0 ? unitTitleDeedDocs[unitTitleDeedDocs.length - 1] : undefined
                    dispatch(setValue({
                        id: formItem.id,
                        value: titleDeedDoc?.attributes?.Type?.[0]?.Type ?? null
                    }))
                    break
                }
            case 'special_requests':
                dispatch(setValue({
                    id: formItem.id,
                    value: currentUnit?.Statuses?.includes('Special Request') === true ? 'Yes' : 'No'
                }))
                break
            case 'tenant_requirements':
                {
                    const allRequirements = Object.keys(currentUnit?.SpecialRequest ?? {})
                    const activeRequirements = allRequirements.filter((requirement) => currentUnit?.SpecialRequest?.[requirement] === true)
                    dispatch(setValue({
                        id: formItem.id,
                        value: activeRequirements
                    }))
                }
                break
            default:
                // path = _.split(formItem.row.id, '.')
                dispatch(setValue({
                    id: formItem.row.id as string,
                    value: _.get(currentUnit, formItem.row.id as string)
                    // value: _.reduce(
                    //     path,
                    //     (object, key) => object?.[key],
                    //     ((currentUnit) ?? {}))
                }))
        }
        if (formItem.row.id === 'Images') {
            if (currentUnit?.Images?.length > 0) {
                dispatch(setImages(_.map((currentUnit)?.Images, (image) => ({ data: image }))))
                dispatch(setImagesLoaded(true))
            }
        }
        /* if (row.id === 'Marketing.DiscountStartDatetime' || row.id === 'Marketing.DiscountEndDatetime') {
            row.default = dayjs(row.default)
            console.log(row.default, typeof row.default)
        }
        if (row.element === 'label-secondary') {
            row.content[1] = row.default
            if (row.id === 'Marketing.DiscountStartDatetime' || row.id === 'Marketing.DiscountEndDatetime') {
                row.content[1] = moment(row.default).format('LL')
            }
        }
        if (row.id === 'edit_marketing_button') {
            row.modal = 'edit_unit_marketing_modal'
        }
        if (row.id === 'Images') {
            dispatch(setImages(_.map((currentUnit)?.attributes?.Images, (image) => ({ data: image }))))
        }
        state[row.id] = row.default
        console.log({ state }) */
    })
    if (currentUnit?.Documents?.length > 0) {
        const filteredDocs = _.filter(currentUnit.Documents, document => ownerDocumentFields(document)?.Type?.[0]?.__component === 'documents.title-deed')
        const docs = _.map(
            filteredDocs,
            (document: any) => document.id
        )
        const titleDeed = ownerDocumentFields(filteredDocs[filteredDocs.length - 1])
        dispatch(setValue({ id: 'Scanned.DocumentYear', value: titleDeed?.Type?.[0]?.Year ?? '' }))
        dispatch(setValue({ id: 'Scanned.DocumentExpiryDate', value: titleDeed?.ExpiryDate ?? '' }))
        dispatch(setDocuments(docs))
        if (titleDeed?.Link != null) {
            dispatch(addUploadedDocument({ field: 'title_deed', base64: titleDeed.Link }))
        }
        dispatch(setTitleDeedLoaded(true))
    }
    dispatch(setDynamicRowShow({ id: 'marketing_separator', state: true }))
    dispatch(setDynamicRowShow({ id: 'marketing_title', state: true }))
    dispatch(setDynamicRowShow({ id: 'edit_marketing_button', state: true }))
    if (currentUnit?.Statuses?.includes('Occupied') === true) {
        dispatch(setDynamicRowShow({ id: 'OccupiedUntil', state: true }))
        dispatch(setValue({
            id: 'OccupiedUntil',
            value: currentUnit.OccupiedUntil // TODO probably wrap OccupiedUntil in dayjs() or not
        }))
    }
    const selectedType = getState().setUnit.staticForm.find(row => row.id === 'Type')?.value
    const selectedCategory = getState().setUnit.staticForm.find(row => row.id === 'UnitCategory')?.value
    const applicability = resolveUnitInfoFieldApplicability(selectedType, selectedCategory)
    if (applicability.GuestBathroom) {
        dispatch(setDynamicRowShow({ id: 'GuestBathroom', state: true }))
        dispatch(setValue({
            id: 'GuestBathroom',
            value: currentUnit.GuestBathroom === true ? 'Yes' : 'No'
        }))

        dispatch(setDynamicRowShow({ id: 'MaidsRoom', state: true }))
        dispatch(setValue({
            id: 'MaidsRoom',
            value: currentUnit.MaidsRoom === true ? 'Yes' : 'No'
        }))
    } else {
        dispatch(setDynamicRowShow({ id: 'GuestBathroom', state: false }))
        dispatch(setDynamicRowShow({ id: 'MaidsRoom', state: false }))
    }
    _.forEach(currentUnit.Landlords, (landlord: any) => {
        if (landlord?.Landlord?.ContactInfo?.FirstName != null && landlord?.Landlord?.ContactInfo?.FirstName !== '') {
            const ownerPassport = pickOwnerDocument(landlord?.Landlord?.Documents, 'passport')
            const ownerEmiratesId = pickOwnerDocument(landlord?.Landlord?.Documents, 'emirates_id')
            dispatch(addOwnerToForm({
                Name: `${String(landlord.Landlord?.ContactInfo?.FirstName)} ${String(landlord.Landlord?.ContactInfo?.FamilyName)}`,
                Phone: landlord.Landlord?.ContactInfo?.Phone,
                Email: landlord.Landlord?.ContactInfo?.Email,
                DateOfBirth: landlord.Landlord?.ContactInfo?.DateOfBirth,
                Country: landlord.Landlord?.ContactInfo?.Country,
                Gender: landlord.Landlord?.ContactInfo?.Gender,
                passport: ownerPassport?.id,
                emirates_id: ownerEmiratesId?.id,
                uploaded_passport: ownerDocumentFields(ownerPassport).Link,
                uploaded_emirates_id: ownerDocumentFields(ownerEmiratesId).Link,
                uploaded_emirates_id_front: ownerDocumentFields(ownerEmiratesId).LinkFront ?? ownerDocumentFields(ownerEmiratesId).Link,
                uploaded_emirates_id_back: ownerDocumentFields(ownerEmiratesId).LinkBack,
                emirates_id_use_single_file: ownerDocumentFields(ownerEmiratesId).LinkFront == null && ownerDocumentFields(ownerEmiratesId).Link != null,
                passport_number: ownerDocumentFields(ownerPassport).Type?.[0]?.Number,
                passport_name: ownerDocumentFields(ownerPassport).Type?.[0]?.Name,
                passport_date_of_birth: ownerDocumentFields(ownerPassport).Type?.[0]?.DateOfBirth,
                passport_gender: ownerDocumentFields(ownerPassport).Type?.[0]?.Gender,
                passport_nationality: ownerDocumentFields(ownerPassport).Type?.[0]?.Nationality,
                passport_expiry_date: ownerDocumentFields(ownerPassport).ExpiryDate,
                emirates_id_number: ownerDocumentFields(ownerEmiratesId).Type?.[0]?.Number,
                emirates_id_name: ownerDocumentFields(ownerEmiratesId).Type?.[0]?.Name,
                emirates_id_date_of_birth: ownerDocumentFields(ownerEmiratesId).Type?.[0]?.DateOfBirth,
                emirates_id_gender: ownerDocumentFields(ownerEmiratesId).Type?.[0]?.Gender,
                emirates_id_nationality: ownerDocumentFields(ownerEmiratesId).Type?.[0]?.Nationality,
                emirates_id_expiry_date: ownerDocumentFields(ownerEmiratesId).ExpiryDate,
                IsDecisionMaker: landlord.IsDecisionMaker
            }))
        }
    })
    const representative = currentUnit?.Landlords?.[0]?.Representatives?.[0]
    if (currentUnit?.Landlords?.[0]?.Representatives?.[0]?.ContactInfo?.FirstName != null) {
        dispatch(setDynamicRowShow({ id: 'representative.ContactInfo.Name', state: true }))
        dispatch(setDynamicRowShow({ id: 'representative.ContactInfo.Phone', state: true }))
        dispatch(setDynamicRowShow({ id: 'representative.ContactInfo.Email', state: true }))
        // dispatch(setDynamicRowShow({ id: 'representative.DocumentNumber', state: true }))
        dispatch(setDynamicRowShow({ id: 'representative.emirates_id', state: true }))
        dispatch(setDynamicRowShow({ id: 'representative.passport', state: true }))
        dispatch(setDynamicRowShow({ id: 'representative.POA', state: true }))
        dispatch(setDynamicRowShow({ id: 'representative.separator', state: true }))

        dispatch(setValue({ id: 'representative.ContactInfo.Name', value: `${String(representative?.ContactInfo?.FirstName)} ${String(representative?.ContactInfo?.FamilyName)}` }))
        dispatch(setValue({ id: 'representative.ContactInfo.Phone', value: representative?.ContactInfo?.Phone }))
        dispatch(setValue({ id: 'representative.ContactInfo.Email', value: representative?.ContactInfo?.Email }))
        // dispatch(setValue({ id: 'representative.DocumentNumber', value: representative?.RefID?.split('-')[representative?.RefID?.split('-')?.length - 1] }))
        const representativePassport = pickOwnerDocument(representative?.Documents, 'passport')
        const representativeEmiratesID = pickOwnerDocument(representative?.Documents, 'emirates_id')
        const representativePOA = currentUnit?.Landlords?.[0]?.PowerOfAttorney
       
        if (representativePassport?.Link != null) {
            dispatch(addUploadedDocument({ field: 'representative.passport', base64: representativePassport.Link }))
        }
        if (representativeEmiratesID?.Link != null) {
            dispatch(addUploadedDocument({ field: 'representative.emirates_id', base64: representativeEmiratesID.Link }))
        }
        if (representativePOA?.Link != null) {
            dispatch(addUploadedDocument({ field: 'representative.POA', base64: representativePOA.Link }))
        }
        dispatch(setRepresentative({
            passport: representativePassport?.id,
            emirates_id: representativeEmiratesID?.id,
            poa: representativePOA?.id
        }))
    }
    dispatch(rerenderModal())
}

/**
 * Prefill the canonical Add Unit form for a new market cycle.
 * This deliberately runs only after the edit filler has completed; it does not
 * change the edit filler or retain its historical listing status.
 */
export const fillValuesForRelist = (unitID: number) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    await dispatch(fillValuesForEdit(unitID))
    const sourceUnit = getState().units.units.find((unit) => unit.id === unitID)
    const sourceDocuments = getDocumentsArray(sourceUnit?.attributes?.Documents)
    const sourceTitleDeed = sourceDocuments.find((document: any) => document?.attributes?.Type?.[0]?.__component === 'documents.title-deed')
    const sourceTitleDeedType = sourceTitleDeed?.attributes?.Type?.[0]
    // Keep editable owner contact data, never the source listing's document IDs.
    const ownerValues = getState().setUnit.owners.map((owner) => ({
        Name: owner.values.Name,
        Phone: owner.values.Phone,
        Email: owner.values.Email,
        IsDecisionMaker: owner.values.IsDecisionMaker
    }))
    dispatch(clearOwners())
    ownerValues.forEach((values) => dispatch(addOwnerToForm(values)))

    // These are listing-cycle state/relations, not physical-property data.
    dispatch(setDocuments([]))
    dispatch(setRepresentative({ passport: undefined, emirates_id: undefined, poa: undefined }))
    const relistHiddenDynamicRows: string[] = ['marketing_separator', 'marketing_title', 'edit_marketing_button', 'OccupiedUntil', 'representative.ContactInfo.Name', 'representative.ContactInfo.Phone', 'representative.ContactInfo.Email', 'representative.emirates_id', 'representative.passport', 'representative.POA', 'representative.separator']
    relistHiddenDynamicRows
        .forEach((id) => dispatch(setDynamicRowShow({ id, state: false })))
    // The canonical Unit-details form accepts compound physical-status values
    // (for example Vacant-Door open), not the Marketing-only "Available" value.
    // Require the admin to select a valid new-cycle status.
    dispatch(setValue({ id: 'Statuses', value: null }))
    // Reset the secondary Mortgage selector too: it is not a valid standalone
    // listing status while the required physical status has not been selected.
    dispatch(setValue({ id: 'Statuses[1]', value: null }))
    dispatch(setValue({ id: 'DisabledTimes', value: [] }))
    if (sourceTitleDeed?.attributes?.Link != null) {
        // The Link is reusable storage data. The historical document ID is intentionally not retained.
        dispatch(setScannedData({
            failedToScan: { url: sourceTitleDeed.attributes.Link },
            relistSourceDocumentId: sourceTitleDeed.id
        }))
        dispatch(addUploadedDocument({ field: 'title_deed', base64: sourceTitleDeed.attributes.Link }))
        dispatch(setTitleDeedLoaded(true))
        dispatch(setValue({ id: 'title_deed', value: documentStatuses.true }))
        dispatch(setValue({ id: 'Scanned.DocumentNumber', value: sourceTitleDeedType?.Number ?? '' }))
        dispatch(setValue({ id: 'Scanned.DocumentYear', value: sourceTitleDeedType?.Year ?? '' }))
        dispatch(setValue({ id: 'Scanned.DocumentType', value: sourceTitleDeedType?.Type ?? 'Unit' }))
        dispatch(setValue({ id: 'titleDeedPropertyType', value: sourceTitleDeedType?.Type ?? null }))
    } else {
        dispatch(setTitleDeedLoaded(false))
        dispatch(setValue({ id: 'title_deed', value: '' }))
    }
    dispatch(rerenderModal())
}

/**
 * @function addOwnerToForm
 * @param {OwnerValues} values
 *
 * @description This function adds a new owner to the owners list and calls rerenderModal to display new owner.
 * If there is at least one owner in the list, a hidden field owners_valid gets value 'Valid' so it is possible to submit the form. Otherwise, validation will be invoked
 */
export const addOwnerToForm = (values: OwnerValues = {}) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { owners } = getState().setUnit
    const index = (owners[owners.length - 1]?.index ?? 0) + 1
    const normalizedValues = { ...values }
    ownerIdentityConflictKeys(normalizedValues).forEach((key) => { normalizedValues[key] = undefined })
    dispatch(addOwner({
        index,
        id: `Owners[${index}]`,
        values: normalizedValues
    }))
    dispatch(rerenderModal())
    if (getState().setUnit.owners.length === 0) {
        dispatch(setValue({ id: 'owners_valid', value: '' }))
    } else {
        dispatch(setValue({ id: 'owners_valid', value: 'Valid' }))
    }
}

/**
 * @function ownerFieldId
 * @param {...string[]}args
 *
 * @description gets owner field id from sources like the ownerFieldParts array
 */
export const ownerFieldId = (...args: string[]): string => {
    return args.join('.')
}

const ownerFieldParts = [
    ['ContactInfo', 'Name'],
    ['ContactInfo', 'Phone'],
    ['ContactInfo', 'Email'],
    ['ContactInfo', 'DateOfBirth'],
    ['ContactInfo', 'Country'],
    ['ContactInfo', 'Gender'],
    ['passport_file'],
    ['passport_number'],
    ['passport_name'],
    ['passport_date_of_birth'],
    ['passport_gender'],
    ['passport_nationality'],
    ['passport_expiry_date'],
    ['emirates_id_front_file'],
    ['emirates_id_back_file'],
    ['emirates_id_number'],
    ['emirates_id_name'],
    ['emirates_id_date_of_birth'],
    ['emirates_id_gender'],
    ['emirates_id_nationality'],
    ['emirates_id_expiry_date'],
    ['IsDecisionMaker'],
    ['separator']
]

/**
 * @function mapOwnerToTemplate
 * @param {Owner} owner
 *
 * @description prepares owner object to be added to form as a set of fields
 */
export const mapOwnerToTemplate = (owner: Owner): Array<{ row: ModalRow<string>, value: any }> => {
    const conflicts = ownerIdentityConflictKeys(owner.values)
    const document = (kind: 'passport' | 'emirates_id') => {
        const prefix = kind === 'passport' ? 'passport' : 'emirates_id'
        const fields = [
            { key: 'number', label: `${kind === 'passport' ? 'Passport' : 'Emirates ID'} number`, value: owner.values[`${prefix}_number` as OwnerValuesKey] },
            { key: 'name', label: 'Holder name', value: owner.values[`${prefix}_name` as OwnerValuesKey] },
            { key: 'date_of_birth', label: 'Holder date of birth', value: owner.values[`${prefix}_date_of_birth` as OwnerValuesKey], type: 'date' as const },
            { key: 'gender', label: 'Holder gender', value: owner.values[`${prefix}_gender` as OwnerValuesKey], type: 'select' as const },
            { key: 'nationality', label: 'Holder nationality', value: owner.values[`${prefix}_nationality` as OwnerValuesKey] },
            { key: 'expiry_date', label: 'Expiry date', value: owner.values[`${prefix}_expiry_date` as OwnerValuesKey], type: 'date' as const }
        ]
        const hasAnyDocument = kind === 'passport'
            ? owner.values.passport_enabled === true || owner.values.passport != null || owner.values.passport_file != null || owner.values.uploaded_passport != null
            : owner.values.emirates_id_enabled === true || owner.values.emirates_id != null || owner.values.emirates_id_front_file != null || owner.values.emirates_id_back_file != null || owner.values.uploaded_emirates_id_front != null || owner.values.uploaded_emirates_id_back != null || owner.values.uploaded_emirates_id != null
        const files = kind === 'passport'
            ? [{ label: 'Passport file', file: owner.values.passport_file, link: owner.values.uploaded_passport }]
            : [
                { label: 'Emirates ID front', file: owner.values.emirates_id_front_file, link: owner.values.uploaded_emirates_id_front ?? owner.values.uploaded_emirates_id },
                { label: 'Emirates ID back', file: owner.values.emirates_id_back_file, link: owner.values.uploaded_emirates_id_back }
            ]
        const filesReady = (kind === 'emirates_id' && owner.values.emirates_id_use_single_file === true ? files.slice(0, 1) : files)
            .every((entry) => entry.file != null || entry.link != null)
        const metadataReady = fields.every((field) => String(field.value ?? '').trim() !== '')
        const status = !hasAnyDocument ? 'Not added' : !filesReady ? 'Upload required' : !metadataReady ? 'Needs completion' : 'Ready'
        return {
            kind,
            title: kind === 'passport' ? 'Passport' : 'Emirates ID',
            enabled: hasAnyDocument,
            expanded: owner.values[`${prefix}_details_open` as OwnerValuesKey] === true || status === 'Needs completion',
            status,
            files,
            useSingleFile: kind === 'emirates_id' && owner.values.emirates_id_use_single_file === true,
            fields
        }
    }
    const documents = [document('passport'), document('emirates_id')]
    const identityDocument = documents.find((item) => item.status === 'Ready') ?? documents.find((item) => item.enabled)
    const summaryName = identityDocument?.fields.find((field) => field.key === 'name')?.value ?? owner.values.Name
    const summary = identityDocument?.status === 'Ready'
        ? `Verified identity: ${String(summaryName ?? 'Owner')}`
        : identityDocument != null ? 'Identity document needs completion' : 'Identity document required'
    return [
        {
            row: {
                id: ownerFieldId(owner.id, 'ContactInfo', 'Name'),
                content: ['Owner name'],
                element: 'input',
                required: true
            },
            value: owner.values.Name
        },
        {
            row: {
                id: ownerFieldId(owner.id, 'ContactInfo', 'Phone'),
                content: ['Owner phone'],
                element: 'input',
                regexp: regExps.isPhone
                // required: true
            },
            value: owner.values.Phone
        },
        {
            row: {
                id: ownerFieldId(owner.id, 'ContactInfo', 'Email'),
                content: ['Owner email'],
                element: 'input',
                regexp: regExps.isEmail
                // required: true
            },
            value: owner.values.Email
        },
        {
            row: {
                id: ownerFieldId(owner.id, 'IdentityDocuments'),
                element: 'owner-identity-documents',
                ownerIdentityDocuments: {
                    summary,
                    conflictKeys: conflicts,
                    conflictValues: {
                        DateOfBirth: owner.values.DateOfBirth,
                        Country: owner.values.Country,
                        Gender: owner.values.Gender
                    },
                    documents
                }
            },
            value: undefined
        },
        {
            row: {
                id: ownerFieldId(owner.id, 'IsDecisionMaker'),
                content: ['Is decision maker', 'Select option'],
                selectors: ['Yes', 'No'],
                element: 'select',
                required: false
            },
            value: owner.values.IsDecisionMaker === true ? 'Yes' : 'No'
        },
        {
            row: {
                id: ownerFieldId(owner.id, 'separator'),
                element: 'separator'
            },
            value: undefined
        }
    ]
}

/**
 * @function applyTitleDeed
 * @param {any[]} scannedDocuments
 *
 * @description the callback that is called after TitleDeed is scanned.
 * Adds owners to the form, fills the TitleDeed related fields in modal.
 * Adds or picks property.
 */
export const applyTitleDeed = (scannedDocuments: any[]) => (dispatch: AppDispatch, getState: () => RootState) => {
    const values = dispatch(renderSetUnitInitialState())

    const resolveTitleDeedArea = (rawArea: unknown): string | undefined => {
        const areaText = String(rawArea ?? '').trim()
        if (areaText.length === 0) return undefined

        const configuredAreas = getState()?.app?.config?.all?.Area ?? []
        const searchedAreas = searchFuse(configuredAreas, areaText, 0.4, [])
        if (searchedAreas?.[0]?.item != null) {
            return searchedAreas[0].item
        }

        const exactMatch = configuredAreas.find((area) => area.toLowerCase().trim() === areaText.toLowerCase())
        return exactMatch ?? areaText
    }

    // const keys = _.keys(values)
    const keys = _.map(getState().setUnit.staticForm, (formItem) => formItem.id)
    
    for (const document of scannedDocuments) {
        const scannedDocument = document?.response?.document ?? {}
        dispatch(setValue({ id: 'Scanned.DocumentYear', value: scannedDocument.registryYear ?? scannedDocument.year ?? scannedDocument.registry_year ?? '' }))
        const scannedExpiryDate = String(scannedDocument.expDate ?? '')
        dispatch(setValue({ id: 'Scanned.DocumentExpiryDate', value: /^\d{6}$/.test(scannedExpiryDate) ? getCorrectExpDate(scannedExpiryDate) : normalizeOwnerScanDate(scannedExpiryDate) ?? '' }))
        const entries = _.toPairs(scannedDocument)
        for (const [key, value] of entries) {
            const formKey = _.find(keys, (keyItem) => keyItem?.toLowerCase() === key.toLowerCase())
            if (formKey != null) {
                switch (formKey) {
                    case 'Number':
                        break
                    case 'Property':
                        // Keep existing property binding if already selected by user.
                        if (getState().setUnit.staticForm.find((storedRow) => storedRow.id === 'Property')?.value != null) {
                            if (getState().setUnit.staticForm.find((storedRow) => storedRow.id === 'Number')?.value == null) {
                                dispatch(setValue({ id: 'Number', value: (value as any)?.number }))
                            }
                            break
                        }
                        const resolvedArea = resolveTitleDeedArea((value as any)?.area)
                    
                        // eslint-disable-next-line
                        const storedProperty = getState().property.properties.find((property) => property.attributes.Area === resolvedArea && property.attributes.Name === (value as any)?.name)
                        if (storedProperty == null) {
                            void dispatch(addProperty({
                                Name: (value as any)?.name ?? resolvedArea ?? (value as any)?.area,
                                Area: resolvedArea,
                                Type: 'Community'
                            }, true))
                                .then((response) => {
                                    dispatch(setValue({ id: 'Property', value: response.response.data.id }))
                          
                                    dispatch(setDynamicDropDown(mapPropertiesForSelect(getState().property.properties)))
                                    dispatch(rerenderModal())
                                }).catch((err) => {
                                    console.log(err)
                                })
                        } else {
                            dispatch(setValue({ id: 'Property', value: storedProperty.id }))
                        }
                        if (getState().setUnit.staticForm.find((storedRow) => storedRow.id === 'Number')?.value == null) {
                            dispatch(setValue({ id: 'Number', value: (value as any)?.number }))
                        }
                        break
                    case 'Type':
                        values.titleDeedPropertyType = value
                        dispatch(setValue({ id: 'titleDeedPropertyType', value: value as string }))
                        if (isMainUnitType(value)) {
                            dispatch(setValue({ id: 'Type', value }))
                            const selectedCategory = getState().setUnit.staticForm.find((storedRow) => storedRow.id === 'UnitCategory')?.value
                            if (selectedCategory != null && !getUnitCategoriesByType(value).includes(selectedCategory)) {
                                dispatch(setValue({ id: 'UnitCategory', value: null }))
                            }
                        }
                        break
                    default:
                        values[formKey] = value
                        dispatch(setValue({ id: formKey, value: value as string }))
                        break
                }
            }
        }
        // if (getState().user.currentUser.Category === 'Landlord') {
        //     _.forEach(/* _.filter( */ document?.response?.document?.owners/* , owner => owner.valid === true */, (owner: any) => {
        //         console.log(owner)
        //         dispatch(addOwnerToForm({ Name: owner.name }))
        //     })
        // }
         //   console.log(values,"values 2")
    }
    dispatch(rerenderModal())
}
